import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { PreferencesProvider } from "./context/PreferencesContext";
import { PendingGenerationsProvider } from "./context/PendingGenerationsContext";
import { ProtectedRoute } from "./components/routing/ProtectedRoute";
import { LazyErrorBoundary } from "./components/routing/LazyErrorBoundary";
// Layouts y páginas críticas: import ESTÁTICO.
// Son las primeras pantallas a las que entra el usuario (/auth, /app, oposiciones).
// Si se cargan con React.lazy, cualquier reinicio del dev server o pequeño fallo
// de red provoca "Failed to fetch dynamically imported module" y pantalla en blanco.
// NO volver a poner estos imports en lazy.
import { AuthLayout } from "./layouts/AuthLayout";
import { LoginFuncionarioPage } from "./pages/auth/funcionarios/LoginFuncionarioPage";
import { RegisterFuncionarioPage } from "./pages/auth/funcionarios/RegisterFuncionarioPage";
import { LoginOpositorPage } from "./pages/auth/opositores/LoginOpositorPage";
import { RegisterOpositorPage } from "./pages/auth/opositores/RegisterOpositorPage";
import { AppLayout } from "./layouts/AppLayout";
import { OposicionesLayout } from "./layouts/OposicionesLayout";
import { OposicionesC1Layout } from "./layouts/OposicionesC1Layout";
import { OposicionesC2Layout } from "./layouts/OposicionesC2Layout";
import { OposicionesIIPPLayout } from "./layouts/OposicionesIIPPLayout";
import { GuardiaCivilLayout } from "./layouts/GuardiaCivilLayout";
import { OposicionesPNLayout } from "./layouts/OposicionesPNLayout";
import { BaseComunLayout } from "./layouts/BaseComunLayout";
import { AdminLayout } from "./layouts/AdminLayout";
import { ChatHomePage } from "./pages/app/ChatHomePage";
import { ChatDetailPage } from "./pages/app/ChatDetailPage";
import { useAuth } from "./hooks/useAuth";
import { getOposicionRouteForBody, OPOSICIONES_BASIC_PLAN } from "./lib/oposicionesAccess";

// Lazy import with retry on transient chunk failures (no full page reload — avoids loops with dev server restarts)
const CHUNK_ERR_RE = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk \d+ failed|ChunkLoadError/i;

function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return React.lazy(async () => {
    const maxAttempts = 3;
    let lastErr: any;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        return await factory();
      } catch (err: any) {
        lastErr = err;
        const msg = String(err?.message || err);
        if (!CHUNK_ERR_RE.test(msg)) throw err;
        // Wait a bit (dev server may be restarting) and retry
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    throw lastErr;
  });
}

// Spinner institucional con clases ESTÁTICAS. Tailwind JIT no puede generar clases
// construidas en runtime como `border-${color}`, por eso los spinners aparecían
// invisibles y el usuario veía "pantalla en blanco" durante cualquier Suspense.
type SpinnerVariant = "primary" | "violet-600" | "blue-600" | "green-600" | "red-600";

const SPINNER_BORDER: Record<SpinnerVariant, string> = {
  "primary": "border-primary",
  "violet-600": "border-violet-600",
  "blue-600": "border-blue-600",
  "green-600": "border-green-600",
  "red-600": "border-red-600",
};

const Spinner = ({ color = "primary" }: { color?: SpinnerVariant | string }) => {
  const variant: SpinnerVariant = (SPINNER_BORDER as any)[color]
    ? (color as SpinnerVariant)
    : "primary";
  return (
    <div
      className="flex flex-col items-center justify-center gap-5 min-h-screen w-full"
      style={{ background: "linear-gradient(135deg,#f0fdfa 0%,#ccfbf1 100%)" }}
    >
      <img
        src="/munilex-logo.webp"
        alt="Munilex"
        width={169}
        height={48}
        decoding="async"
        style={{ objectFit: "contain" }}
      />
      <div
        className={`animate-spin h-8 w-8 border-4 ${SPINNER_BORDER[variant]} border-t-transparent rounded-full`}
        aria-label="Cargando"
        role="status"
      />
      <span className="text-sm font-medium text-slate-600">Cargando…</span>
    </div>
  );
};

const SuspenseWrap = ({ children, color }: { children: React.ReactNode; color?: string }) => (
  <React.Suspense fallback={<Spinner color={color} />}>{children}</React.Suspense>
);

const APP_INDEX_ROUTES: Record<string, string> = {
  [OPOSICIONES_BASIC_PLAN]: "/app/base-comun",
  base_comun: "/app/base-comun",
  oposiciones: "/app/oposiciones/chat",
  oposiciones_c1: "/app/oposiciones-c1/chat",
  oposiciones_c2: "/app/oposiciones-c2/chat",
  oposiciones_iipp: "/app/oposiciones-iipp/chat",
  oposiciones_guardia_civil: "/app/guardia-civil/chat",
  oposiciones_policia_nacional: "/app/policia-nacional/chat",
  oposiciones_generico: "/app/base-comun",
};

function AppIndexRedirect() {
  const { profile } = useAuth();
  if (!profile) return <Spinner />;

  const isPrivileged = profile.is_betatester || profile.role === "admin" || profile.role === "super_admin";
  if (isPrivileged) return <Navigate to="/app/funcionarios" replace />;

  if (profile.plan === OPOSICIONES_BASIC_PLAN) {
    return <Navigate to={`${getOposicionRouteForBody(profile.opositor_body)}/chat`} replace />;
  }

  if (profile.opositor_body === 'generico') {
    return <Navigate to="/app/base-comun" replace />;
  }

  return <Navigate to={APP_INDEX_ROUTES[profile.plan || "free"] || "/app/funcionarios"} replace />;
}

// Layouts críticos = imports estáticos arriba. NO añadir lazy aquí.

// Lazy load admin pages
const AdminDashboardPage = lazyWithRetry(() => import("./pages/admin/AdminDashboardPage").then(m => ({ default: m.AdminDashboardPage })));
const AdminUsersPage = lazyWithRetry(() => import("./pages/admin/AdminUsersPage").then(m => ({ default: m.AdminUsersPage })));
const AdminUsagePage = lazyWithRetry(() => import("./pages/admin/AdminUsagePage").then(m => ({ default: m.AdminUsagePage })));
const AdminBillingPage = lazyWithRetry(() => import("./pages/admin/AdminBillingPage").then(m => ({ default: m.AdminBillingPage })));
const AdminSecurityPage = lazyWithRetry(() => import("./pages/admin/AdminSecurityPage").then(m => ({ default: m.AdminSecurityPage })));
const AdminMessagesPage = lazyWithRetry(() => import("./pages/admin/AdminMessagesPage").then(m => ({ default: m.AdminMessagesPage })));
const AdminConsultasPage = lazyWithRetry(() => import("./pages/admin/AdminConsultasPage").then(m => ({ default: m.AdminConsultasPage })));
const AdminDocumentModelsPage = lazyWithRetry(() => import("./pages/admin/AdminDocumentModelsPage"));
const AdminExportPage = lazyWithRetry(() => import("./pages/admin/AdminExportPage"));
const AdminOposicionesPage = lazyWithRetry(() => import("./pages/admin/AdminOposicionesPage"));
const AdminGCVisualsPage = lazyWithRetry(() => import("./pages/admin/AdminGCVisualsPage"));
const AdminContenidoOposicionesPage = lazyWithRetry(() => import("./pages/admin/AdminContenidoOposicionesPage"));
const AdminPlansSettingsPage = lazyWithRetry(() => import("./pages/admin/AdminPlansSettingsPage"));
const AdminErrorsPage = lazyWithRetry(() => import("./pages/admin/AdminErrorsPage").then(m => ({ default: m.AdminErrorsPage })));
const AdminAcademicAuditPage = lazyWithRetry(() => import("./pages/admin/AdminAcademicAuditPage"));
const AdminBancoPreguntasPage = lazyWithRetry(() => import("./pages/admin/AdminBancoPreguntasPage"));
const AdminSalesMarketingAIPage = lazyWithRetry(() => import("./pages/admin/AdminSalesMarketingAIPage").then(m => ({ default: m.AdminSalesMarketingAIPage })));

// The first login and registration forms are intentionally static imports. They
// must remain reachable even if a stale browser cache cannot fetch a lazy chunk.
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/auth/ForgotPasswordPage").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/auth/ResetPasswordPage").then(m => ({ default: m.ResetPasswordPage })));
const ForgotPasswordOpositorPage = lazyWithRetry(() => import("./pages/auth/opositores/ForgotPasswordOpositorPage").then(m => ({ default: m.ForgotPasswordOpositorPage })));
const ResetPasswordOpositorPage = lazyWithRetry(() => import("./pages/auth/opositores/ResetPasswordOpositorPage").then(m => ({ default: m.ResetPasswordOpositorPage })));
const PendingConfirmationOpositorPage = lazyWithRetry(() => import("./pages/auth/opositores/PendingConfirmationOpositorPage").then(m => ({ default: m.PendingConfirmationOpositorPage })));
const VerifiedPage = lazyWithRetry(() => import("./pages/auth/VerifiedPage").then(m => ({ default: m.VerifiedPage })));
const VerifyRequiredPage = lazyWithRetry(() => import("./pages/auth/VerifyRequiredPage").then(m => ({ default: m.VerifyRequiredPage })));
const PendingConfirmationPage = lazyWithRetry(() => import("./pages/auth/PendingConfirmationPage").then(m => ({ default: m.PendingConfirmationPage })));

// Lazy load app pages (ChatHomePage / ChatDetailPage son estáticas arriba)
const SecurityPage = lazyWithRetry(() => import("./pages/app/SecurityPage").then(m => ({ default: m.SecurityPage })));
const SubscriptionPage = lazyWithRetry(() => import("./pages/app/SubscriptionPage"));
const DownloadAnalysis = lazyWithRetry(() => import("./pages/app/DownloadAnalysis"));
const MyDocumentsPage = lazyWithRetry(() => import("./pages/app/MyDocumentsPage"));
const SavedMessagesPage = lazyWithRetry(() => import("./pages/app/SavedMessagesPage"));
const DocumentEditorPage = lazyWithRetry(() => import("./pages/app/DocumentEditorPage"));
const RedactorPage = lazyWithRetry(() => import("./pages/app/RedactorPage"));
const JurisprudenciaPage = lazyWithRetry(() => import("./pages/app/funcionarios/JurisprudenciaPage").then(m => ({ default: m.JurisprudenciaPage })));
const FuncionariosDashboardPage = lazyWithRetry(() => import("./pages/app/funcionarios/FuncionariosDashboardPage").then(m => ({ default: m.FuncionariosDashboardPage })));
const AccountSettingsPage = lazyWithRetry(() => import("./pages/app/AccountSettingsPage").then(m => ({ default: m.AccountSettingsPage })));

// Lazy load public pages
const ContactPage = lazyWithRetry(() => import("./pages/ContactPage").then(m => ({ default: m.ContactPage })));
const ContactOpositoresPage = lazyWithRetry(() => import("./pages/ContactOpositoresPage").then(m => ({ default: m.ContactOpositoresPage })));
const PricingPage = lazyWithRetry(() => import("./pages/PricingPage").then(m => ({ default: m.PricingPage })));
const PricingOpositoresPage = lazyWithRetry(() => import("./pages/PricingOpositoresPage").then(m => ({ default: m.PricingOpositoresPage })));
const PricingFuncionariosPage = lazyWithRetry(() => import("./pages/PricingFuncionariosPage").then(m => ({ default: m.PricingFuncionariosPage })));
const PaymentSuccessPage = lazyWithRetry(() => import("./pages/app/PaymentSuccessPage").then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazyWithRetry(() => import("./pages/app/PaymentCancelPage").then(m => ({ default: m.PaymentCancelPage })));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

// Lazy load oposiciones pages
const OposicionesDashboard = lazyWithRetry(() => import("./pages/app/oposiciones/OposicionesDashboard"));
const TestExamPage = lazyWithRetry(() => import("./pages/app/oposiciones/TestExamPage"));
const FlashcardsPage = lazyWithRetry(() => import("./pages/app/oposiciones/FlashcardsPage"));
const ResourcesPage = lazyWithRetry(() => import("./pages/app/oposiciones/ResourcesPage"));
const ProgressPage = lazyWithRetry(() => import("./pages/app/oposiciones/ProgressPage"));
const CasePracticePage = lazyWithRetry(() => import("./pages/app/oposiciones/CasePracticePage"));
const OralPracticePage = lazyWithRetry(() => import("./pages/app/oposiciones/OralPracticePage"));
const PresentationViewerPage = lazyWithRetry(() => import("./pages/app/oposiciones/PresentationViewerPage"));

// Lazy load oposiciones C1 pages
const OposicionesC1Dashboard = lazyWithRetry(() => import("./pages/app/oposiciones-c1/OposicionesC1Dashboard"));
const TestExamC1Page = lazyWithRetry(() => import("./pages/app/oposiciones-c1/TestExamC1Page"));
const FlashcardsC1Page = lazyWithRetry(() => import("./pages/app/oposiciones-c1/FlashcardsC1Page"));
const ResourcesC1Page = lazyWithRetry(() => import("./pages/app/oposiciones-c1/ResourcesC1Page"));
const ProgressC1Page = lazyWithRetry(() => import("./pages/app/oposiciones-c1/ProgressC1Page"));
const CasePracticeC1Page = lazyWithRetry(() => import("./pages/app/oposiciones-c1/CasePracticeC1Page"));

// Lazy load Guardia Civil pages (GuardiaCivilLayout estático arriba)
const OposicionesGCDashboard = lazyWithRetry(() => import("./pages/app/guardia-civil/OposicionesGCDashboard"));
const TestExamGCPage = lazyWithRetry(() => import("./pages/app/guardia-civil/TestExamGCPage"));
const FlashcardsGCPage = lazyWithRetry(() => import("./pages/app/guardia-civil/FlashcardsGCPage"));
const ResourcesGCPage = lazyWithRetry(() => import("./pages/app/guardia-civil/ResourcesGCPage"));
const ProgressGCPage = lazyWithRetry(() => import("./pages/app/guardia-civil/ProgressGCPage"));
const ExamenOficialGCPage = lazyWithRetry(() => import("./pages/app/guardia-civil/ExamenOficialGCPage"));
const EnglishB1Page = lazyWithRetry(() => import("./pages/app/guardia-civil/EnglishB1Page"));
const LenguaPage = lazyWithRetry(() => import("./pages/app/guardia-civil/LenguaPage"));
const PsicotecnicoPage = lazyWithRetry(() => import("./pages/app/guardia-civil/PsicotecnicoPage"));

// Lazy load Policia Nacional pages (OposicionesPNLayout estático arriba)
const OposicionesPNDashboard = lazyWithRetry(() => import("./pages/app/policia-nacional/OposicionesPNDashboard"));
const TestExamPNPage = lazyWithRetry(() => import("./pages/app/policia-nacional/TestExamPNPage"));
const FlashcardsPNPage = lazyWithRetry(() => import("./pages/app/policia-nacional/FlashcardsPNPage"));
const ResourcesPNPage = lazyWithRetry(() => import("./pages/app/policia-nacional/ResourcesPNPage"));
const ProgressPNPage = lazyWithRetry(() => import("./pages/app/policia-nacional/ProgressPNPage"));
const EnglishPNPage = lazyWithRetry(() => import("./pages/app/policia-nacional/EnglishPNPage"));
const LenguaPNPage = lazyWithRetry(() => import("./pages/app/policia-nacional/LenguaPNPage"));
const PsicotecnicoPNPage = lazyWithRetry(() => import("./pages/app/policia-nacional/PsicotecnicoPNPage"));

// Lazy load IIPP pages (OposicionesIIPPLayout estático arriba)
const OposicionesIIPPDashboard = lazyWithRetry(() => import("./pages/app/oposiciones-iipp/OposicionesIIPPDashboard"));
const TestExamIIPPPage = lazyWithRetry(() => import("./pages/app/oposiciones-iipp/TestExamIIPPPage"));
const FlashcardsIIPPPage = lazyWithRetry(() => import("./pages/app/oposiciones-iipp/FlashcardsIIPPPage"));
const ResourcesIIPPPage = lazyWithRetry(() => import("./pages/app/oposiciones-iipp/ResourcesIIPPPage"));
const ProgressIIPPPage = lazyWithRetry(() => import("./pages/app/oposiciones-iipp/ProgressIIPPPage"));
const CasePracticeIIPPPage = lazyWithRetry(() => import("./pages/app/oposiciones-iipp/CasePracticeIIPPPage"));

// Lazy load C2 pages (OposicionesC2Layout estático arriba)
const OposicionesC2Dashboard = lazyWithRetry(() => import("./pages/app/oposiciones-c2/OposicionesC2Dashboard"));
const TestExamC2Page = lazyWithRetry(() => import("./pages/app/oposiciones-c2/TestExamC2Page"));
const FlashcardsC2Page = lazyWithRetry(() => import("./pages/app/oposiciones-c2/FlashcardsC2Page"));
const ResourcesC2Page = lazyWithRetry(() => import("./pages/app/oposiciones-c2/ResourcesC2Page"));
const ProgressC2Page = lazyWithRetry(() => import("./pages/app/oposiciones-c2/ProgressC2Page"));
const CasePracticeC2Page = lazyWithRetry(() => import("./pages/app/oposiciones-c2/CasePracticeC2Page"));

// Lazy load Base Común pages
const BaseComunDashboard = lazyWithRetry(() => import("./pages/app/base-comun/BaseComunDashboard"));
const TestExamGenericoPage = lazyWithRetry(() => import("./pages/app/base-comun/TestExamGenericoPage"));
const FlashcardsGenericoPage = lazyWithRetry(() => import("./pages/app/base-comun/FlashcardsGenericoPage"));
const ResourcesGenericoPage = lazyWithRetry(() => import("./pages/app/base-comun/ResourcesGenericoPage"));
const ProgressGenericoPage = lazyWithRetry(() => import("./pages/app/base-comun/ProgressGenericoPage"));
const CasePracticeGenericoPage = lazyWithRetry(() => import("./pages/app/base-comun/CasePracticeGenericoPage"));

const queryClient = new QueryClient();

const App = () => (
  <LazyErrorBoundary>
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <PreferencesProvider>
        <PendingGenerationsProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
          <Routes>
            {/* Root redirects to app */}
            <Route path="/" element={<Navigate to="/app" replace />} />
            <Route path="/login" element={<Navigate to="/auth/login" replace />} />
            <Route path="/register" element={<Navigate to="/auth/register" replace />} />

            {/* Public pages */}
            <Route path="/pricing" element={<SuspenseWrap><PricingPage /></SuspenseWrap>} />
            <Route path="/pricing/opositores" element={<SuspenseWrap><PricingOpositoresPage /></SuspenseWrap>} />
            <Route path="/pricing/funcionarios" element={<SuspenseWrap><PricingFuncionariosPage /></SuspenseWrap>} />
            <Route path="/contact" element={<SuspenseWrap><ContactPage /></SuspenseWrap>} />
            <Route path="/contact/opositores" element={<SuspenseWrap><ContactOpositoresPage /></SuspenseWrap>} />
            <Route path="/app/oposiciones/contacto" element={<SuspenseWrap><ContactOpositoresPage /></SuspenseWrap>} />
            {/* Payment result pages */}
            <Route path="/payment-success" element={
              <ProtectedRoute>
                <SuspenseWrap><PaymentSuccessPage /></SuspenseWrap>
              </ProtectedRoute>
            } />
            <Route path="/payment-cancel" element={
              <ProtectedRoute>
                <SuspenseWrap><PaymentCancelPage /></SuspenseWrap>
              </ProtectedRoute>
            } />

            {/* Auth routes — dos flujos completamente independientes, sin selector intermedio */}
            <Route path="/auth" element={<AuthLayout />}>
              <Route index element={<Navigate to="/auth/funcionarios/login" replace />} />
              {/* Legacy compatibility — flujo por defecto: funcionarios */}
              <Route path="login" element={<Navigate to="/auth/funcionarios/login" replace />} />
              <Route path="register" element={<Navigate to="/auth/funcionarios/register" replace />} />
              {/* Funcionarios */}
              <Route path="funcionarios/login" element={<SuspenseWrap><LoginFuncionarioPage /></SuspenseWrap>} />
              <Route path="funcionarios/register" element={<SuspenseWrap><RegisterFuncionarioPage /></SuspenseWrap>} />
              {/* Opositores */}
              <Route path="opositores/login" element={<SuspenseWrap><LoginOpositorPage /></SuspenseWrap>} />
              <Route path="opositores/register" element={<SuspenseWrap><RegisterOpositorPage /></SuspenseWrap>} />
              <Route path="opositores/forgot-password" element={<SuspenseWrap><ForgotPasswordOpositorPage /></SuspenseWrap>} />
              <Route path="opositores/reset-password" element={<SuspenseWrap><ResetPasswordOpositorPage /></SuspenseWrap>} />
              <Route path="opositores/pending-confirmation" element={<SuspenseWrap><PendingConfirmationOpositorPage /></SuspenseWrap>} />
              {/* Compartidas (funcionarios por defecto) */}
              <Route path="forgot-password" element={<SuspenseWrap><ForgotPasswordPage /></SuspenseWrap>} />
              <Route path="reset-password" element={<SuspenseWrap><ResetPasswordPage /></SuspenseWrap>} />
              <Route path="verified" element={<SuspenseWrap><VerifiedPage /></SuspenseWrap>} />
              <Route path="verify-required" element={<SuspenseWrap><VerifyRequiredPage /></SuspenseWrap>} />
              <Route path="pending-confirmation" element={<SuspenseWrap><PendingConfirmationPage /></SuspenseWrap>} />
            </Route>

            {/* App routes (user) */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AppIndexRedirect />} />
              <Route path="chat" element={<SuspenseWrap><ChatHomePage /></SuspenseWrap>} />
              <Route path="chat/:chatId" element={<SuspenseWrap><ChatDetailPage /></SuspenseWrap>} />
              <Route path=":chatId" element={<SuspenseWrap><ChatDetailPage /></SuspenseWrap>} />
              <Route path="security" element={<SuspenseWrap><SecurityPage /></SuspenseWrap>} />
              <Route path="subscription" element={<SuspenseWrap><SubscriptionPage /></SuspenseWrap>} />
              <Route path="download-analysis" element={<SuspenseWrap><DownloadAnalysis /></SuspenseWrap>} />
              <Route path="documents" element={<SuspenseWrap><MyDocumentsPage /></SuspenseWrap>} />
              <Route path="saved" element={<SuspenseWrap><SavedMessagesPage /></SuspenseWrap>} />
              <Route path="editor" element={<SuspenseWrap><DocumentEditorPage /></SuspenseWrap>} />
              <Route path="redactor" element={<SuspenseWrap><RedactorPage /></SuspenseWrap>} />
              <Route path="funcionarios" element={<SuspenseWrap><FuncionariosDashboardPage /></SuspenseWrap>} />
              <Route path="funcionarios/inicio" element={<SuspenseWrap><FuncionariosDashboardPage /></SuspenseWrap>} />
              <Route path="funcionarios/jurisprudencia" element={<SuspenseWrap><JurisprudenciaPage /></SuspenseWrap>} />
              <Route path="jurisprudencia" element={<SuspenseWrap><JurisprudenciaPage /></SuspenseWrap>} />
              <Route path="resoluciones" element={<SuspenseWrap><JurisprudenciaPage /></SuspenseWrap>} />
              <Route path="settings" element={<SuspenseWrap><AccountSettingsPage /></SuspenseWrap>} />
            </Route>

            {/* Oposiciones routes */}
            <Route
              path="/app/oposiciones"
              element={
                <ProtectedRoute>
                  <OposicionesLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SuspenseWrap color="violet-600"><OposicionesDashboard /></SuspenseWrap>} />
              <Route path="chat" element={<SuspenseWrap><ChatHomePage /></SuspenseWrap>} />
              <Route path="chat/:chatId" element={<SuspenseWrap><ChatDetailPage /></SuspenseWrap>} />
              <Route path="test" element={<SuspenseWrap color="violet-600"><TestExamPage /></SuspenseWrap>} />
              <Route path="flashcards" element={<SuspenseWrap color="violet-600"><FlashcardsPage /></SuspenseWrap>} />
              <Route path="recursos" element={<SuspenseWrap color="violet-600"><ResourcesPage /></SuspenseWrap>} />
              <Route path="progreso" element={<SuspenseWrap color="violet-600"><ProgressPage /></SuspenseWrap>} />
              <Route path="caso" element={<SuspenseWrap color="violet-600"><CasePracticePage /></SuspenseWrap>} />
              <Route path="oral" element={<SuspenseWrap color="violet-600"><OralPracticePage /></SuspenseWrap>} />
              <Route path="presentacion-demo" element={<SuspenseWrap color="violet-600"><PresentationViewerPage /></SuspenseWrap>} />
            </Route>

            <Route path="/app/presentacion-demo" element={<SuspenseWrap><PresentationViewerPage /></SuspenseWrap>} />

            {/* Oposiciones C1 routes */}
            <Route
              path="/app/oposiciones-c1"
              element={
                <ProtectedRoute>
                  <OposicionesC1Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SuspenseWrap color="blue-600"><OposicionesC1Dashboard /></SuspenseWrap>} />
              <Route path="chat" element={<SuspenseWrap><ChatHomePage /></SuspenseWrap>} />
              <Route path="chat/:chatId" element={<SuspenseWrap><ChatDetailPage /></SuspenseWrap>} />
              <Route path="test" element={<SuspenseWrap color="blue-600"><TestExamC1Page /></SuspenseWrap>} />
              <Route path="flashcards" element={<SuspenseWrap color="blue-600"><FlashcardsC1Page /></SuspenseWrap>} />
              <Route path="recursos" element={<SuspenseWrap color="blue-600"><ResourcesC1Page /></SuspenseWrap>} />
              <Route path="progreso" element={<SuspenseWrap color="blue-600"><ProgressC1Page /></SuspenseWrap>} />
              <Route path="caso" element={<SuspenseWrap color="blue-600"><CasePracticeC1Page /></SuspenseWrap>} />
            </Route>

            {/* Guardia Civil routes */}
            <Route
              path="/app/guardia-civil"
              element={
                <ProtectedRoute>
                  <GuardiaCivilLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SuspenseWrap color="green-600"><OposicionesGCDashboard /></SuspenseWrap>} />
              <Route path="chat" element={<SuspenseWrap><ChatHomePage /></SuspenseWrap>} />
              <Route path="chat/:chatId" element={<SuspenseWrap><ChatDetailPage /></SuspenseWrap>} />
              <Route path="test" element={<SuspenseWrap color="green-600"><TestExamGCPage /></SuspenseWrap>} />
              <Route path="flashcards" element={<SuspenseWrap color="green-600"><FlashcardsGCPage /></SuspenseWrap>} />
              <Route path="recursos" element={<SuspenseWrap color="green-600"><ResourcesGCPage /></SuspenseWrap>} />
              <Route path="progreso" element={<SuspenseWrap color="green-600"><ProgressGCPage /></SuspenseWrap>} />
              <Route path="caso" element={<SuspenseWrap color="green-600"><ExamenOficialGCPage /></SuspenseWrap>} />
              <Route path="ingles" element={<SuspenseWrap color="green-600"><EnglishB1Page /></SuspenseWrap>} />
              <Route path="lengua" element={<SuspenseWrap color="green-600"><LenguaPage /></SuspenseWrap>} />
              <Route path="psicotecnico" element={<SuspenseWrap color="green-600"><PsicotecnicoPage /></SuspenseWrap>} />
            </Route>

            {/* Policia Nacional routes */}
            <Route
              path="/app/policia-nacional"
              element={
                <ProtectedRoute>
                  <OposicionesPNLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SuspenseWrap color="sky-700"><OposicionesPNDashboard /></SuspenseWrap>} />
              <Route path="chat" element={<SuspenseWrap><ChatHomePage /></SuspenseWrap>} />
              <Route path="chat/:chatId" element={<SuspenseWrap><ChatDetailPage /></SuspenseWrap>} />
              <Route path="test" element={<SuspenseWrap color="sky-700"><TestExamPNPage /></SuspenseWrap>} />
              <Route path="flashcards" element={<SuspenseWrap color="sky-700"><FlashcardsPNPage /></SuspenseWrap>} />
              <Route path="recursos" element={<SuspenseWrap color="sky-700"><ResourcesPNPage /></SuspenseWrap>} />
              <Route path="progreso" element={<SuspenseWrap color="sky-700"><ProgressPNPage /></SuspenseWrap>} />
              <Route path="ingles" element={<SuspenseWrap color="sky-700"><EnglishPNPage /></SuspenseWrap>} />
              <Route path="lengua" element={<SuspenseWrap color="sky-700"><LenguaPNPage /></SuspenseWrap>} />
              <Route path="psicotecnico" element={<SuspenseWrap color="sky-700"><PsicotecnicoPNPage /></SuspenseWrap>} />
            </Route>

            {/* IIPP routes */}
            <Route
              path="/app/oposiciones-iipp"
              element={
                <ProtectedRoute>
                  <OposicionesIIPPLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SuspenseWrap color="red-600"><OposicionesIIPPDashboard /></SuspenseWrap>} />
              <Route path="chat" element={<SuspenseWrap><ChatHomePage /></SuspenseWrap>} />
              <Route path="chat/:chatId" element={<SuspenseWrap><ChatDetailPage /></SuspenseWrap>} />
              <Route path="test" element={<SuspenseWrap color="red-600"><TestExamIIPPPage /></SuspenseWrap>} />
              <Route path="flashcards" element={<SuspenseWrap color="red-600"><FlashcardsIIPPPage /></SuspenseWrap>} />
              <Route path="recursos" element={<SuspenseWrap color="red-600"><ResourcesIIPPPage /></SuspenseWrap>} />
              <Route path="progreso" element={<SuspenseWrap color="red-600"><ProgressIIPPPage /></SuspenseWrap>} />
              <Route path="caso" element={<SuspenseWrap color="red-600"><CasePracticeIIPPPage /></SuspenseWrap>} />
            </Route>

            {/* C2 Auxiliar Estado routes */}
            <Route
              path="/app/oposiciones-c2"
              element={
                <ProtectedRoute>
                  <OposicionesC2Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SuspenseWrap color="blue-600"><OposicionesC2Dashboard /></SuspenseWrap>} />
              <Route path="chat" element={<SuspenseWrap><ChatHomePage /></SuspenseWrap>} />
              <Route path="chat/:chatId" element={<SuspenseWrap><ChatDetailPage /></SuspenseWrap>} />
              <Route path="test" element={<SuspenseWrap color="blue-600"><TestExamC2Page /></SuspenseWrap>} />
              <Route path="flashcards" element={<SuspenseWrap color="blue-600"><FlashcardsC2Page /></SuspenseWrap>} />
              <Route path="recursos" element={<SuspenseWrap color="blue-600"><ResourcesC2Page /></SuspenseWrap>} />
              <Route path="progreso" element={<SuspenseWrap color="blue-600"><ProgressC2Page /></SuspenseWrap>} />
              <Route path="caso" element={<SuspenseWrap color="blue-600"><CasePracticeC2Page /></SuspenseWrap>} />
            </Route>

            {/* Base Común — Temas Compartidos routes */}
            <Route
              path="/app/base-comun"
              element={
                <ProtectedRoute>
                  <BaseComunLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SuspenseWrap color="amber-600"><BaseComunDashboard /></SuspenseWrap>} />
              <Route path="chat" element={<SuspenseWrap><ChatHomePage /></SuspenseWrap>} />
              <Route path="chat/:chatId" element={<SuspenseWrap><ChatDetailPage /></SuspenseWrap>} />
              <Route path="test" element={<SuspenseWrap color="amber-600"><TestExamGenericoPage /></SuspenseWrap>} />
              <Route path="flashcards" element={<SuspenseWrap color="amber-600"><FlashcardsGenericoPage /></SuspenseWrap>} />
              <Route path="recursos" element={<SuspenseWrap color="amber-600"><ResourcesGenericoPage /></SuspenseWrap>} />
              <Route path="progreso" element={<SuspenseWrap color="amber-600"><ProgressGenericoPage /></SuspenseWrap>} />
              <Route path="caso" element={<SuspenseWrap color="amber-600"><CasePracticeGenericoPage /></SuspenseWrap>} />
            </Route>

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<SuspenseWrap><AdminDashboardPage /></SuspenseWrap>} />
              <Route path="users" element={<SuspenseWrap><AdminUsersPage /></SuspenseWrap>} />
              <Route path="consultas" element={<SuspenseWrap><AdminConsultasPage /></SuspenseWrap>} />
              <Route path="messages" element={<SuspenseWrap><AdminMessagesPage /></SuspenseWrap>} />
              <Route path="sales" element={<Navigate to="/admin/sales-marketing-ai/pipeline" replace />} />
              <Route path="marketing" element={<Navigate to="/admin/sales-marketing-ai/pipeline" replace />} />
              <Route path="usage" element={<SuspenseWrap><AdminUsagePage /></SuspenseWrap>} />
              <Route path="models" element={<SuspenseWrap><AdminDocumentModelsPage /></SuspenseWrap>} />
              <Route path="agents" element={<Navigate to="/admin/sales-marketing-ai/agents" replace />} />
              <Route path="billing" element={<SuspenseWrap><AdminBillingPage /></SuspenseWrap>} />
              <Route path="security" element={<SuspenseWrap><AdminSecurityPage /></SuspenseWrap>} />
              <Route path="errors" element={<SuspenseWrap><AdminErrorsPage /></SuspenseWrap>} />
              <Route path="academic-audit" element={<SuspenseWrap><AdminAcademicAuditPage /></SuspenseWrap>} />
              <Route path="banco-preguntas" element={<SuspenseWrap><AdminBancoPreguntasPage /></SuspenseWrap>} />
              <Route path="sales-marketing-ai" element={<Navigate to="/admin/sales-marketing-ai/pipeline" replace />} />
              <Route path="sales-marketing-ai/pipeline" element={<SuspenseWrap><AdminSalesMarketingAIPage /></SuspenseWrap>} />
              <Route path="sales-marketing-ai/sources" element={<SuspenseWrap><AdminSalesMarketingAIPage /></SuspenseWrap>} />
              <Route path="sales-marketing-ai/agents" element={<SuspenseWrap><AdminSalesMarketingAIPage /></SuspenseWrap>} />
              <Route path="sales-marketing-ai/playbooks" element={<SuspenseWrap><AdminSalesMarketingAIPage /></SuspenseWrap>} />
              <Route path="sales-marketing-ai/contexts" element={<SuspenseWrap><AdminSalesMarketingAIPage /></SuspenseWrap>} />
              <Route path="sales-marketing-ai/runs" element={<SuspenseWrap><AdminSalesMarketingAIPage /></SuspenseWrap>} />
              <Route path="sales-marketing-ai/alerts" element={<SuspenseWrap><AdminSalesMarketingAIPage /></SuspenseWrap>} />
              <Route path="export" element={<SuspenseWrap><AdminExportPage /></SuspenseWrap>} />
              <Route path="settings/plans" element={<SuspenseWrap><AdminPlansSettingsPage /></SuspenseWrap>} />
              {/* Oposiciones — nueva estructura agrupada */}
              <Route path="oposiciones" element={<Navigate to="/admin/oposiciones/actividad" replace />} />
              <Route path="oposiciones/actividad" element={<SuspenseWrap><AdminOposicionesPage /></SuspenseWrap>} />
              <Route path="oposiciones/contenido" element={<SuspenseWrap><AdminContenidoOposicionesPage /></SuspenseWrap>} />
              {/* Compatibilidad con URL antigua */}
              <Route path="gc" element={<Navigate to="/admin/oposiciones/contenido?cuerpo=guardia-civil" replace />} />
              <Route path="gc-visuals" element={<Navigate to="/admin/oposiciones/contenido?cuerpo=guardia-civil" replace />} />
              <Route path="gc" element={<Navigate to="/admin/oposiciones/contenido?cuerpo=guardia-civil" replace />} />
              <Route path="pn" element={<Navigate to="/admin/oposiciones/contenido?cuerpo=policia-nacional" replace />} />
              <Route path="questions" element={<Navigate to="/admin/banco-preguntas" replace />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<SuspenseWrap><NotFound /></SuspenseWrap>} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
      </PendingGenerationsProvider>
      </PreferencesProvider>
    </AuthProvider>
  </QueryClientProvider>
  </LazyErrorBoundary>
);

export default App;
